import { CreateBibleStudySection } from '../api/models/BibleStudySection';
import _ from 'lodash';
import { apiConfigs, APIRequestConfig } from '../api/APIConfigs';
import {
  AudioModule,
  DocumentModule,
  ImageModule,
  MeditationModule,
  ModuleType,
  ScriptureModule,
  SectionModule,
  TextModule,
  VideoModule,
} from '../api/models/BibleStudyModule';
import { BibleScripture } from '../api/models/BibleVerse';
import { UploadResult } from '../api/models/UploadResult';
import { initialModuleFormState } from '../modules/bible-studies/containers/BibleStudyFormContainer';
import { getMuxHlsPlayback } from './mux-utilities';
import { ModuleFormState } from '../modules/bible-studies/bible-study-form';

type RequestFn = <T>(config: APIRequestConfig) => Promise<T>;

type ModuleLibrary<T> = {
  [key in ModuleType]: T;
};

export type BibleRangePayload = {
  book: string;
  version?: string;
  fromChapter: number;
  fromVerse: number;
  toChapter: number;
  toVerse: number;
};

/**
 * Creates a module request body library
 * @param moduleForm
 * @returns
 */
export const getModuleRequestBodyLibrary = (moduleForm: ModuleFormState): ModuleLibrary<SectionModule> => ({
  image: {
    moduleType: 'image',
    name: moduleForm.title,
    url: moduleForm.url,
    tag: moduleForm.dayTag,
  } as ImageModule,
  video: {
    moduleType: 'video',
    muxID: moduleForm.url,
    title: moduleForm.title,
    tag: moduleForm.dayTag,
  } as VideoModule,
  scripture: {
    moduleType: 'scripture',
    title: moduleForm.title,
    scriptureIDList: moduleForm.scriptures
      // Filter out empty [scriptureIds]
      ?.filter(scripture => scripture.scriptureIds.length)
      .map(scriptures => scriptures.scriptureIds),
    translationID: moduleForm.translationId,
    tag: moduleForm.dayTag,
  } as ScriptureModule,
  document: {
    moduleType: 'document',
    name: moduleForm.title,
    url: moduleForm.url,
    tag: moduleForm.dayTag,
  } as DocumentModule,
  qanda: {
    moduleType: 'qanda',
    scriptureIDList: moduleForm.scriptures
      // Filter out empty [scriptureIds]
      ?.filter(scripture => scripture.scriptureIds.length)
      .map(scriptures => scriptures.scriptureIds),
    title: moduleForm.title,
    questionAndAnswers: moduleForm.questionAndAnswers,
    translationID: moduleForm.translationId,
    tag: moduleForm.dayTag,
  } as MeditationModule,
  audio: {
    moduleType: 'audio',
    muxID: moduleForm.url,
    title: moduleForm.title,
    tag: moduleForm.dayTag,
    fileName: moduleForm.fileName,
  } as AudioModule,
  text: {
    moduleType: 'text',
    title: moduleForm.title,
    content: moduleForm.content,
    tag: moduleForm.dayTag,
  } as TextModule,
});

/**
 * Library that's responsible for uploading module file
 * based on module type that returns the uploaded file path.
 */
export const moduleUploadLib: ModuleLibrary<Function> = {
  image: async (request: RequestFn, src: File) => {
    const result = await request<UploadResult>(apiConfigs.uploadImage(src));
    return result.filePath;
  },
  video: async (request: RequestFn, src: File) => {
    const result = await request<UploadResult>(apiConfigs.uploadVideo(src));
    return result.muxID;
  },
  document: async (request: RequestFn, src: File) => {
    const result = await request<UploadResult>(apiConfigs.uploadDocument(src));
    return result.filePath;
  },
  audio: async (request: RequestFn, src: File) => {
    const result = await request<UploadResult>(apiConfigs.uploadAudio(src));
    return result.muxID;
  },
  scripture: async () => {
    // nothing to upload
    return '';
  },
  qanda: async () => {
    // nothing to upload
    return '';
  },
  text: async () => {
    // nothing to upload
    return '';
  },
};

/**
 * Generates module form state based on the stored module from the server
 */
export const generateModuleFormState = (args: {
  sections: CreateBibleStudySection[];
  sectionIndex: number;
  moduleIndex: number;
}): ModuleFormState => {
  const { sections, sectionIndex, moduleIndex } = args;
  const section = sections[sectionIndex];
  const module = section.modules[moduleIndex];

  const baseModuleFormState: ModuleFormState = {
    ...initialModuleFormState,
    sectionIndex,
    moduleIndex,
    weekTag: section.tag || section.name,
    dayTag: module.tag || '',
  };

  switch (module.moduleType) {
    case 'image':
      return {
        ...baseModuleFormState,
        type: module.moduleType,
        title: module.name || '',
        url: module.url,
      };
    case 'video':
      return {
        ...baseModuleFormState,
        type: module.moduleType,
        title: module.title || '',
        url: module.muxID ? getMuxHlsPlayback(module.muxID) : '',
      };
    case 'scripture':
    case 'qanda':
      if (!module.scriptureIDList) {
        throw new Error('Module does not have scriptures');
      }

      const formState: ModuleFormState = {
        ...baseModuleFormState,
      };

      formState.title = module.title || '';
      formState.translationId = module.translationID || 'NIV';

      // Scriptures should have preloaded unique ID to serve as keys
      formState.scriptures = module.scriptureIDList.map((scriptureIds, i) => {
        const scripture = generateBibleRangePayloadFromScriptureIDs(scriptureIds);

        let initialScriptureForm;
        if (validateBibleRangePayload(scripture)) {
          initialScriptureForm = {
            book: scripture.book,
            from: {
              chapter: scripture.fromChapter,
              verse: scripture.fromVerse,
            },
            to: {
              chapter: scripture.toChapter,
              verse: scripture.toVerse,
            },
          };
        }

        return {
          id: `${i + 1}`,
          scriptureIds,
          initialScriptureForm,
        };
      });

      if (module.moduleType === 'scripture') {
        formState.type = 'scripture';
      } else {
        formState.type = 'qanda';
        formState.questionAndAnswers = module.questionAndAnswers;
        formState.scriptureVisible = !!formState.scriptures.length;
      }

      return formState;
    case 'document':
      return {
        ...baseModuleFormState,
        type: module.moduleType,
        title: module.name || '',
        url: module.url,
      };
    case 'audio':
      return {
        ...baseModuleFormState,
        type: module.moduleType,
        title: module.title || '',
        //url: module.muxID ? getMuxHlsPlayback(module.muxID) : '',
        url: module.muxID ? module.muxID : '',
        fileName: module.fileName || '',
      };
    case 'text':
      return {
        ...baseModuleFormState,
        title: module.title,
        type: module.moduleType,
        content: module.content,
      };
    default:
      return {
        ...baseModuleFormState,
      };
  }
};

/** When set, chapter/verse bounds come from the form — not the first/last verse in the API array (which can disagree). */
export type ScriptureRangeBoundsOverride = {
  fromChapter: number;
  fromVerse: number;
  toChapter: number;
  toVerse: number;
};

export const getScriptureIdsFromBibleScriptures = (
  scriptures: BibleScripture[],
  canonicalBookId?: string | null,
  rangeBounds?: ScriptureRangeBoundsOverride | null
) => {
  if (!scriptures.length) {
    return [];
  }

  const from = scriptures[0];
  const to = scriptures[scriptures.length - 1];

  const getBookIdFromVerse = (scripture: any): string => {
    if (scripture.book_id) return scripture.book_id;
    if (scripture.bookId) return scripture.bookId;
    if (typeof scripture.chapter === 'string' && scripture.chapter.includes('.')) {
      return scripture.chapter.split('.')[0];
    }
    if (typeof scripture.id === 'string' && scripture.id.includes('.')) {
      return scripture.id.split('.')[0];
    }
    return '';
  };

  const bookForTuple = (scripture: any) => {
    const trimmed = canonicalBookId?.trim();
    if (trimmed) return trimmed;
    return getBookIdFromVerse(scripture);
  };

  const bookForPayload = canonicalBookId?.trim() || bookForTuple(from);

  const useBoundsOverride =
    rangeBounds &&
    rangeBounds.fromChapter > 0 &&
    rangeBounds.fromVerse > 0 &&
    rangeBounds.toChapter > 0 &&
    rangeBounds.toVerse > 0;

  if (useBoundsOverride) {
    return [
      bookForPayload,
      String(rangeBounds.fromChapter),
      String(rangeBounds.fromVerse),
      '-',
      bookForPayload,
      String(rangeBounds.toChapter),
      String(rangeBounds.toVerse),
    ];
  }

  const getChapter = (scripture: any): string => {
    if (typeof scripture.chapter === 'number') return scripture.chapter.toString();
    if (typeof scripture.chapter === 'string') {
      const split = scripture.chapter.split('.');
      return split[1] || scripture.chapter;
    }
    if (typeof scripture.id === 'string') {
      const split = scripture.id.split('.');
      if (split.length >= 2) return split[1];
    }
    return '';
  };

  return [
    bookForTuple(from),
    getChapter(from),
    from.verse.toString(),
    '-',
    bookForTuple(to),
    getChapter(to),
    to.verse.toString(),
  ];
};

export type BibleBookCatalogRow = { id: string };

export const resolveBookIdToCatalogId = (stored: string, books: BibleBookCatalogRow[]): string => {
  const s = (stored ?? '').trim();
  if (!s || !books.length) return s;

  if (books.some(b => b.id === s)) return s;
  const byICase = books.find(b => b.id.toLowerCase() === s.toLowerCase());
  if (byICase) return byICase.id;

  const lower = s.toLowerCase();
  const suffixHits = books.filter(b => b.id.toLowerCase().endsWith(lower));
  if (suffixHits.length === 1) return suffixHits[0].id;

  if (suffixHits.length > 1) {
    const upper = s.toUpperCase();
    for (const prefix of ['1', '2', '3', '4', '5']) {
      const candidate = `${prefix}${upper}`;
      const hit = books.find(b => b.id === candidate);
      if (hit) return hit.id;
    }
  }

  if (s.length >= 2) {
    const prefixHits = books.filter(b => b.id.length > s.length && b.id.toLowerCase().startsWith(lower));
    if (prefixHits.length === 1) return prefixHits[0].id;
  }

  return s;
};

export const normalizeScriptureTupleWithBooksCatalog = (
  scriptureIds: string[],
  books: BibleBookCatalogRow[]
): string[] => {
  if (!scriptureIds.length || scriptureIds.length < 7) return scriptureIds;
  const out = [...scriptureIds];
  out[0] = resolveBookIdToCatalogId(scriptureIds[0], books);
  out[4] = resolveBookIdToCatalogId((scriptureIds[4] ?? scriptureIds[0]).trim(), books);
  return out;
};

export const generateBibleRangePayloadFromScriptureIDs = (scriptureIDs: string[]): BibleRangePayload => {
  return {
    book: scriptureIDs[0],
    fromChapter: parseInt(scriptureIDs[1], 10),
    fromVerse: parseInt(scriptureIDs[2], 10),
    toChapter: parseInt(scriptureIDs[5], 10),
    toVerse: parseInt(scriptureIDs[6], 10),
  };
};

export const validateBibleRangePayload = (payload: BibleRangePayload): boolean => {
  const VALID_PAYLOAD_MAP_LENGTH = 5;
  const entries = Object.entries(payload);
  const check1 = entries.length === VALID_PAYLOAD_MAP_LENGTH;
  const check2 = entries.slice(1).every(([key, value]) => !_.isNaN(value));
  return check1 && check2;
};
